import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Minimal admin confirmation dialog with an optional reason field.
 * Used by M2B customer-action buttons.
 */
export default function ConfirmActionDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  destructive = false,
  reasonRequired = false,
  reasonMinLength = 1,
  reasonLabel = "Reason (optional)",
  reasonPlaceholder = "Short note explaining why",
  testId = "confirm-action-dialog",
  busy = false,
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const minLen = reasonRequired ? Math.max(reasonMinLength, 1) : 0;
  const canConfirm = !busy && (minLen === 0 || reason.trim().length >= minLen);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={testId} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid={`${testId}-title`}>{title}</DialogTitle>
          {description && (
            <DialogDescription data-testid={`${testId}-description`}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="mt-2">
          <label className="text-xs uppercase tracking-wider text-brand-muted">{reasonLabel}</label>
          <Textarea
            data-testid={`${testId}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={reasonPlaceholder}
            rows={3}
            className="mt-1"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid={`${testId}-cancel`} variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            data-testid={`${testId}-confirm`}
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm}
            variant={destructive ? "destructive" : "default"}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
